import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import ProductCard from './ProductCard';

const ProductGrid = ({ products, selectedProducts, onSelectProduct, onRemoveProduct, filters }) => {
  const [sortBy, setSortBy] = useState('rating');
  const [sortOrder, setSortOrder] = useState('desc');

  const sortOptions = [
    { value: 'rating', label: 'Rating', icon: 'Star' },
    { value: 'price', label: 'Price', icon: 'DollarSign' },
    { value: 'performance', label: 'Performance', icon: 'BarChart3' },
    { value: 'popularity', label: 'Popularity', icon: 'TrendingUp' },
    { value: 'newest', label: 'Newest', icon: 'Clock' }
  ];

  const filteredAndSortedProducts = useMemo(() => {
    let filtered = [...products];

    // Apply search filter
    if (filters?.search) {
      const searchTerm = filters?.search?.toLowerCase();
      filtered = filtered?.filter(product =>
        product?.name?.toLowerCase()?.includes(searchTerm) ||
        product?.brand?.toLowerCase()?.includes(searchTerm) ||
        product?.type?.toLowerCase()?.includes(searchTerm)
      );
    }

    // Apply price filter
    if (filters?.price?.min !== undefined && filters?.price?.min !== '') {
      filtered = filtered?.filter(product => product?.currentPrice >= filters?.price?.min);
    }
    if (filters?.price?.max !== undefined && filters?.price?.max !== '') {
      filtered = filtered?.filter(product => product?.currentPrice <= filters?.price?.max);
    }

    // Apply brand filter
    if (filters?.brands && filters?.brands?.length > 0) {
      filtered = filtered?.filter(product => filters?.brands?.includes(product?.brand));
    }

    // Apply type filter
    if (filters?.types && filters?.types?.length > 0) {
      filtered = filtered?.filter(product => filters?.types?.includes(product?.type));
    }

    // Apply rating filter
    if (filters?.rating) {
      filtered = filtered?.filter(product => product?.rating >= filters?.rating);
    }

    // Apply feature filters
    if (filters?.features) {
      Object.entries(filters?.features)?.forEach(([feature, enabled]) => {
        if (enabled) {
          filtered = filtered?.filter(product => product?.[feature]);
        }
      });
    }

    // Apply performance filter
    if (filters?.performance?.min) {
      filtered = filtered?.filter(product => product?.performanceScore >= filters?.performance?.min);
    }

    // Apply weight filter
    if (filters?.weight?.min !== null && filters?.weight?.max !== null) {
      filtered = filtered?.filter(product => 
        product?.weight >= (filters?.weight?.min || 0) && 
        product?.weight <= (filters?.weight?.max || 999)
      );
    }

    // Apply sorting
    filtered?.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'rating':
          aValue = a?.rating;
          bValue = b?.rating;
          break;
        case 'price':
          aValue = a?.currentPrice;
          bValue = b?.currentPrice;
          break;
        case 'performance':
          aValue = a?.performanceScore;
          bValue = b?.performanceScore;
          break;
        case 'popularity':
          aValue = a?.reviewCount;
          bValue = b?.reviewCount;
          break;
        case 'newest':
          aValue = new Date(a.releaseDate);
          bValue = new Date(b.releaseDate);
          break;
        default:
          return 0;
      }

      if (sortOrder === 'asc') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });

    return filtered;
  }, [products, filters, sortBy, sortOrder]);

  const handleSort = (newSortBy) => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('desc');
    }
  };

  const isProductSelected = (productId) => {
    return selectedProducts?.some(p => p?.id === productId);
  };

  return (
    <div className="flex-1">
      {/* Header */}
      <div className="bg-white rounded-lg border border-border p-4 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-text-primary">
              Available Products
            </h2>
            <p className="text-sm text-muted-foreground">
              {filteredAndSortedProducts?.length} products found
            </p>
          </div>

          {/* Sort Options */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-muted-foreground">Sort by:</span>
            <div className="flex items-center space-x-1">
              {sortOptions?.map((option) => (
                <Button
                  key={option?.value}
                  variant={sortBy === option?.value ? "default" : "ghost"}
                  size="sm"
                  iconName={option?.icon}
                  iconPosition="left"
                  onClick={() => handleSort(option?.value)}
                  className="text-xs"
                >
                  {option?.label}
                  {sortBy === option?.value && (
                    <Icon 
                      name={sortOrder === 'asc' ? "ArrowUp" : "ArrowDown"} 
                      size={12} 
                      className="ml-1" 
                    />
                  )}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {/* Selected Products Summary */}
        {selectedProducts?.length > 0 && (
          <div className="mt-4 p-3 bg-primary/5 rounded-lg border border-primary/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Icon name="GitCompare" size={16} className="text-primary" />
                <span className="text-sm font-medium text-primary">
                  {selectedProducts?.length} product{selectedProducts?.length !== 1 ? 's' : ''} selected for comparison
                </span>
              </div>
              <Button
                variant="outline"
                size="sm"
                iconName="Eye"
                iconPosition="left"
                onClick={() => {
                  document.getElementById('comparison-table')?.scrollIntoView({ 
                    behavior: 'smooth' 
                  });
                }}
              >
                View Comparison
              </Button>
            </div>
          </div>
        )}
      </div>
      {/* Product Grid */}
      {filteredAndSortedProducts?.length === 0 ? (
        <div className="bg-white rounded-lg border border-border p-12 text-center">
          <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-text-primary mb-2">
            No Products Found
          </h3>
          <p className="text-muted-foreground mb-4">
            Try adjusting your filters or search terms to find more products.
          </p>
          <Button
            variant="outline"
            iconName="RotateCcw"
            iconPosition="left"
            onClick={() => window.location?.reload()}
          >
            Reset Filters
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredAndSortedProducts?.map((product) => (
            <ProductCard
              key={product?.id}
              product={product}
              isSelected={isProductSelected(product?.id)}
              onSelect={onSelectProduct}
              onRemove={onRemoveProduct}
              showAddButton={selectedProducts?.length < 4}
            />
          ))}
        </div>
      )}
      {/* Load More */}
      {filteredAndSortedProducts?.length > 0 && filteredAndSortedProducts?.length >= 12 && (
        <div className="text-center mt-8">
          <Button
            variant="outline"
            iconName="ChevronDown"
            iconPosition="right"
          >
            Load More Products
          </Button>
        </div>
      )}
    </div>
  );
};

export default ProductGrid;